import { Link } from 'react-router-dom'

/*
 * Shared educational content for the peptide calculator. Rendered on the home page
 * and /calculator so both carry real depth (not just the tool) — this is the
 * substantive, original content that supports ranking for "peptide calculator",
 * "peptide reconstitution calculator", and related terms. Educational only.
 */
export default function CalcGuide() {
  return (
    <section className="calc-guide">
      <h2>How to use the peptide calculator</h2>
      <p>
        This peptide reconstitution calculator turns the one relationship people get stuck on — vial size,
        bacteriostatic water, and dose — into a single number: exactly how many units to draw on an insulin
        syringe. You don’t need to remember any formulas. Enter three things and read the result:
      </p>
      <ol className="calc-steps">
        <li>
          <strong>Peptide in the vial (mg).</strong> The total milligrams of peptide in your vial, from the label —
          e.g. 5&nbsp;mg or 10&nbsp;mg.
        </li>
        <li>
          <strong>Bacteriostatic water (mL).</strong> How much water you’re adding to reconstitute it. This is your
          choice — it changes how many units you draw, not the dose itself. 1–3&nbsp;mL is typical.
        </li>
        <li>
          <strong>Target dose.</strong> The amount you want per injection, in micrograms (mcg) or milligrams (mg).
        </li>
        <li>
          <strong>Read the units to draw.</strong> The calculator shows the exact fill line on a U-100 insulin
          syringe, plus your concentration, how many doses the vial holds, and a plain-English summary.
        </li>
      </ol>

      <h2>A worked example</h2>
      <p>
        Say you have a <strong>5&nbsp;mg</strong> vial and add <strong>2&nbsp;mL</strong> of bacteriostatic water,
        and you want a <strong>250&nbsp;mcg</strong> dose:
      </p>
      <ul className="calc-math">
        <li>5&nbsp;mg × 1,000 = <strong>5,000&nbsp;mcg</strong> of peptide in the vial</li>
        <li>5,000&nbsp;mcg ÷ 2&nbsp;mL = <strong>2,500&nbsp;mcg/mL</strong> (your concentration)</li>
        <li>2,500 ÷ 100 = <strong>25&nbsp;mcg per unit</strong> on a U-100 syringe</li>
        <li>250&nbsp;mcg ÷ 25 = <strong>10 units</strong> to draw</li>
      </ul>
      <p>
        That same vial gives you 20 doses of 250&nbsp;mcg. Change the water to 1&nbsp;mL and the dose becomes
        5 units; change it to 3&nbsp;mL and it becomes 15 units — the dose is identical, only the number you draw
        changes. The <Link to="/peptide-dosage-guide">dosage guide</Link> walks through this step by step, and the{' '}
        <Link to="/peptide-dosage-chart">peptide dosage chart</Link> has ready-made tables by vial size.
      </p>

      <h2>How much bacteriostatic water should I add?</h2>
      <p>
        More water spreads a small dose across more units, which makes tiny amounts easier to measure accurately;
        less water concentrates it into fewer units. Neither changes how much peptide is in each dose — only the
        reading on the barrel. A good rule of thumb is to pick a water amount that puts your usual dose at a round,
        easy-to-read number of units (say 10–50), so you’re never squinting between the marks. The calculator’s
        “Help me pick water” mode works this out for you automatically.
      </p>

      <h2>Reading units on a U-100 insulin syringe</h2>
      <p>
        Peptides are drawn with a <strong>U-100 insulin syringe</strong>, where 100 units equals 1&nbsp;mL, so each
        unit is 0.01&nbsp;mL. The 0.3&nbsp;mL (30-unit) and 0.5&nbsp;mL (50-unit) barrels are marked every single
        unit, which makes small draws easier to read than the 1&nbsp;mL (100-unit) barrel, which is marked every two
        units. “Units” are a volume on the barrel — not a fixed amount of peptide — so how much peptide sits in each
        unit depends entirely on your concentration. See the <Link to="/glossary/insulin-syringe">glossary</Link>{' '}
        for the terms.
      </p>

      <h2>Single peptide or a blend</h2>
      <p>
        For one peptide in a vial, use the single calculator above. If you’ve combined multiple peptides in one vial
        (a blend) and want to know how much of each is in a given draw, switch to the blend mode or open the full{' '}
        <Link to="/blend">peptide blend calculator</Link>. Either way, you can save any calculation to your free
        account and build a <Link to="/calendar">dosing calendar</Link> with reminders so you never miss a dose.
      </p>

      <p className="muted small">
        This peptide calculator is an educational math tool only and is not medical advice. Always follow the
        guidance of a qualified healthcare provider and your product’s labeling.
      </p>
    </section>
  )
}
